import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Flex,
  Grid,
  Icon,
  IconButton,
  Image,
  Link,
  Text,
  VStack,
  HStack,
  Tag,
  TagLabel,
  Wrap,
  WrapItem,
  useColorModeValue,
  Tooltip as ChakraTooltip,
} from '@chakra-ui/react';
import { ExternalLinkIcon, PhoneIcon } from '@chakra-ui/icons';
import {
  FaMapMarkerAlt,
  FaGlobe,
  FaStar,
  FaUsers,
  FaArrowUp,
  FaArrowDown,
  FaCircle,
  FaChevronUp,
  FaChevronDown,
  FaStore,
} from 'react-icons/fa';

const MAX_VISIBLE_COMPETITORS = 4;

const typeEmojis = {
  restaurant: '🍽️',
  wine_bar: '🍷',
  vegan_restaurant: '🌱',
  vegetarian_restaurant: '🥗',
  bar: '🍸',
  food: '🍴',
  cafe: '☕',
  market: '🏪',
  deli: '🥪',
  asian_restaurant: '🥢',
  thai_restaurant: '🇹🇭',
  indian_restaurant: '🍛',
  mediterranean_restaurant: '🫒',
  american_restaurant: '🍔',
  fine_dining_restaurant: '✨',
  brunch_restaurant: '🍳',
  middle_eastern_restaurant: '🧆',
  chinese_restaurant: '🥡',
  meal_takeaway: '📦',
  food_store: '🏪',
  catering_service: '👨‍🍳',
  food_delivery: '🚚',
};

const formatBusinessType = (type) =>
  type
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const getCompetitorImageUrl = (competitor, competitorImages) =>
  competitorImages?.[competitor.maps_uri] ||
  competitor.demo_image_url ||
  null;

const getVisibleCompetitors = (allAbove, allBelow, scrollOffset) => {
  const totalAbove = allAbove.length;
  const totalBelow = allBelow.length;

  if (totalAbove + totalBelow <= MAX_VISIBLE_COMPETITORS) {
    return {
      visibleAbove: allAbove,
      visibleBelow: allBelow,
      canScrollUp: false,
      canScrollDown: false,
    };
  }

  let aboveCount = Math.min(Math.ceil(MAX_VISIBLE_COMPETITORS / 2), totalAbove);
  let belowCount = Math.min(MAX_VISIBLE_COMPETITORS - aboveCount, totalBelow);

  if (aboveCount + belowCount < MAX_VISIBLE_COMPETITORS && totalAbove > aboveCount) {
    aboveCount = Math.min(MAX_VISIBLE_COMPETITORS - belowCount, totalAbove);
  }

  const aboveStart = Math.min(scrollOffset, Math.max(0, totalAbove - aboveCount));
  const belowStart = scrollOffset;

  const visibleAbove = allAbove.slice(aboveStart, aboveStart + aboveCount);
  const visibleBelow = allBelow.slice(belowStart, belowStart + belowCount);

  return {
    visibleAbove,
    visibleBelow,
    canScrollUp: aboveStart > 0 || belowStart > 0,
    canScrollDown:
      aboveStart + aboveCount < totalAbove ||
      belowStart + belowCount < totalBelow,
  };
};

const Panel = React.forwardRef(({ children, ...rest }, ref) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');

  return (
    <Box
      ref={ref}
      bg={cardBg}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="xl"
      overflow="hidden"
      display="flex"
      flexDirection="column"
      {...rest}
    >
      {children}
    </Box>
  );
});

Panel.displayName = 'Panel';

const getWebsiteLabel = (uri) => {
  try {
    return new URL(uri).hostname.replace(/^www\./, '');
  } catch {
    return uri;
  }
};

const sortBusinessTypes = (types = [], primaryType) => {
  const uniqueTypes = [...new Set(types.filter(Boolean))];
  if (!primaryType || !uniqueTypes.includes(primaryType)) {
    return uniqueTypes;
  }

  return [primaryType, ...uniqueTypes.filter((type) => type !== primaryType)];
};

const DetailItem = ({
  icon,
  iconBg,
  iconColor,
  label,
  value,
  href,
  isExternal = false,
}) => {
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const labelColor = useColorModeValue('gray.500', 'gray.400');
  const linkHoverBg = useColorModeValue('gray.50', 'gray.700');
  const externalIconColor = useColorModeValue('gray.400', 'gray.500');

  const content = (
    <Flex align="flex-start" gap={3} py={2.5}>
      <Flex
        w="32px"
        h="32px"
        bg={iconBg}
        borderRadius="md"
        justify="center"
        align="center"
        flexShrink={0}
      >
        <Icon as={icon} color={iconColor} boxSize={3.5} />
      </Flex>

      <Box flex={1} minW={0}>
        <Text
          fontSize="2xs"
          fontWeight="600"
          color={labelColor}
          textTransform="uppercase"
          letterSpacing="0.06em"
          mb={0.5}
        >
          {label}
        </Text>
        <Text
          fontSize="sm"
          fontWeight="500"
          color={textColor}
          lineHeight="1.45"
          wordBreak="break-word"
        >
          {value}
        </Text>
      </Box>

      {isExternal && (
        <Flex w="20px" h="20px" justify="center" align="center" flexShrink={0} mt={4}>
          <ExternalLinkIcon boxSize={3} color={externalIconColor} />
        </Flex>
      )}
    </Flex>
  );

  if (href) {
    return (
      <Link
        href={href}
        isExternal={isExternal}
        display="block"
        borderRadius="lg"
        px={1}
        mx={-1}
        _hover={{ textDecoration: 'none', bg: linkHoverBg }}
        transition="background 0.15s ease"
      >
        {content}
      </Link>
    );
  }

  return content;
};

const CompetitorAvatar = ({ competitor, imageUrl }) => {
  const [imageError, setImageError] = useState(false);
  const placeholderBg = useColorModeValue('gray.100', 'gray.700');
  const placeholderColor = useColorModeValue('gray.400', 'gray.500');

  return (
    <Flex
      w="28px"
      h="28px"
      borderRadius="md"
      overflow="hidden"
      flexShrink={0}
      bg={placeholderBg}
      justify="center"
      align="center"
    >
      {imageUrl && !imageError ? (
        <Image
          src={imageUrl}
          alt=""
          objectFit="cover"
          w="full"
          h="full"
          onError={() => setImageError(true)}
        />
      ) : (
        <Icon as={FaStore} boxSize={2.5} color={placeholderColor} />
      )}
    </Flex>
  );
};

const NeighborRow = ({
  competitor,
  score,
  direction,
  onSelect,
  demoMode,
  competitorImages,
}) => {
  const isAbove = direction === 'above';
  const hoverBg = useColorModeValue(
    isAbove ? 'green.50' : 'red.50',
    isAbove ? 'green.900' : 'red.900'
  );
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const metaColor = useColorModeValue('gray.500', 'gray.400');
  const dividerColor = useColorModeValue('gray.100', 'gray.700');
  const indicatorBg = useColorModeValue(
    isAbove ? 'green.100' : 'red.100',
    isAbove ? 'green.800' : 'red.800'
  );
  const indicatorColor = useColorModeValue(
    isAbove ? 'green.600' : 'red.500',
    isAbove ? 'green.200' : 'red.300'
  );
  const dotColor = useColorModeValue('gray.300', 'gray.600');
  const imageUrl = getCompetitorImageUrl(competitor, competitorImages);

  return (
    <ChakraTooltip
      label={`Open ${competitor.name}`}
      hasArrow
      placement="top"
      openDelay={300}
      fontSize="xs"
      hidden={demoMode}
    >
      <Flex
        align="center"
        gap={2}
        py={1.5}
        px={1}
        cursor="pointer"
        borderBottomWidth="1px"
        borderColor={dividerColor}
        onClick={() => onSelect(competitor)}
        _hover={{ bg: hoverBg }}
        _last={{ borderBottom: 'none' }}
        transition="background 0.15s ease"
      >
        <Flex
          w="20px"
          h="20px"
          bg={indicatorBg}
          color={indicatorColor}
          borderRadius="full"
          justify="center"
          align="center"
          flexShrink={0}
        >
          <Icon as={isAbove ? FaArrowUp : FaArrowDown} boxSize={2} />
        </Flex>

        <CompetitorAvatar competitor={competitor} imageUrl={imageUrl} />

        <Box flex={1} minW={0}>
          <Text fontSize="xs" fontWeight="600" color={textColor} noOfLines={1} lineHeight="1.2">
            {competitor.name}
          </Text>
          <HStack spacing={1} fontSize="2xs" color={metaColor} mt={0.5} align="center">
            <HStack spacing={0.5} align="center">
              <Flex
                w="12px"
                h="12px"
                justify="center"
                align="center"
                flexShrink={0}
                transform="translateY(-1px)"
              >
                <Icon as={FaStar} boxSize={2.5} color="yellow.400" />
              </Flex>
              <Text mb={0} fontWeight="600" lineHeight="1">
                {(competitor.rating_avg || 0).toFixed(1)}
              </Text>
            </HStack>
            <Text mb={0} color={dotColor}>·</Text>
            <Text mb={0} noOfLines={1}>{(competitor.rating_count || 0).toLocaleString()}</Text>
          </HStack>
        </Box>

        <Text mb={0} fontSize="sm" fontWeight="700" color={textColor} lineHeight="1" flexShrink={0}>
          {score}
        </Text>
      </Flex>
    </ChakraTooltip>
  );
};

const CompetitorsPanel = ({
  business,
  scoredBusinesses,
  currentScoredIdx,
  scoreRank,
  demoMode,
  onSelectCompetitor,
  competitorImages,
  panelRef,
}) => {
  const [scrollOffset, setScrollOffset] = useState(0);
  const sectionLabelColor = useColorModeValue('gray.500', 'gray.400');
  const headerColor = useColorModeValue('gray.800', 'gray.100');
  const categoryDivider = useColorModeValue('gray.100', 'gray.700');
  const youBg = useColorModeValue('blue.50', 'blue.900');
  const youBorder = useColorModeValue('blue.200', 'blue.600');
  const youText = useColorModeValue('blue.700', 'blue.200');
  const youAccent = useColorModeValue('blue.500', 'blue.300');
  const navBg = useColorModeValue('white', 'gray.800');
  const navBorder = useColorModeValue('blue.200', 'blue.600');
  const navHoverBg = useColorModeValue('blue.100', 'blue.800');

  useEffect(() => {
    setScrollOffset(0);
  }, [business?.maps_uri]);

  const allAbove =
    currentScoredIdx > 0
      ? scoredBusinesses
          .slice(0, currentScoredIdx)
          .reverse()
          .map(({ business: comp, score }) => ({ business: comp, score }))
      : [];
  const allBelow =
    currentScoredIdx >= 0 && currentScoredIdx < scoredBusinesses.length - 1
      ? scoredBusinesses
          .slice(currentScoredIdx + 1)
          .map(({ business: comp, score }) => ({ business: comp, score }))
      : [];

  const { visibleAbove, visibleBelow, canScrollUp, canScrollDown } =
    getVisibleCompetitors(allAbove, allBelow, scrollOffset);

  const youImageUrl = getCompetitorImageUrl(business, competitorImages);

  return (
    <Panel ref={panelRef}>
      <Flex
        px={3}
        pt={2.5}
        pb={2}
        justify="space-between"
        align="center"
        borderBottomWidth="1px"
        borderColor={categoryDivider}
        flexShrink={0}
      >
        <HStack spacing={1.5}>
          <Icon as={FaUsers} color="blue.500" boxSize={3.5} />
          <Text mb={0}
            fontSize="2xs"
            fontWeight="700"
            color={headerColor}
            textTransform="uppercase"
            letterSpacing="0.06em"
          >
            Competitors
          </Text>
        </HStack>
        <Text mb={0} fontSize="2xs" color={sectionLabelColor} fontWeight="500" noOfLines={1}>
          #{scoreRank}/{scoredBusinesses.length}
        </Text>
      </Flex>

      <Box px={3} py={1.5} display="flex" flexDirection="column">
        {canScrollUp && (
          <Flex justify="center" pb={1}>
            <IconButton
              aria-label="Show higher-ranked competitors"
              icon={<Icon as={FaChevronUp} boxSize={2.5} />}
              size="xs"
              variant="outline"
              bg={navBg}
              borderColor={navBorder}
              color={youAccent}
              _hover={{ bg: navHoverBg }}
              onClick={() => setScrollOffset((prev) => Math.max(0, prev - 1))}
            />
          </Flex>
        )}

        {visibleAbove.map(({ business: comp, score: compScore }, idx) => (
          <NeighborRow
            key={comp.maps_uri || `above-${idx}`}
            competitor={comp}
            score={compScore}
            direction="above"
            onSelect={onSelectCompetitor}
            demoMode={demoMode}
            competitorImages={competitorImages}
          />
        ))}

        <Flex
          align="center"
          gap={2}
          py={1.5}
          px={1.5}
          my={0.5}
          borderRadius="md"
          bg={youBg}
          borderWidth="1px"
          borderColor={youBorder}
        >
          <Flex
            w="20px"
            h="20px"
            bg="blue.500"
            color="white"
            borderRadius="full"
            justify="center"
            align="center"
            flexShrink={0}
          >
            <Icon as={FaCircle} boxSize={1.5} />
          </Flex>

          <CompetitorAvatar competitor={business} imageUrl={youImageUrl} />

          <Box flex={1} minW={0}>
            <Text fontSize="xs" fontWeight="700" color={youText} noOfLines={1} lineHeight="1.2">
              {business.name}
            </Text>
            <Text fontSize="2xs" color={youAccent} fontWeight="600" lineHeight="1">
              You · #{scoreRank}
            </Text>
          </Box>

          <Text mb={0} fontSize="sm" fontWeight="700" color={youText} lineHeight="1" flexShrink={0}>
            {scoredBusinesses[currentScoredIdx]?.score ?? 0}
          </Text>
        </Flex>

        {visibleBelow.map(({ business: comp, score: compScore }, idx) => (
          <NeighborRow
            key={comp.maps_uri || `below-${idx}`}
            competitor={comp}
            score={compScore}
            direction="below"
            onSelect={onSelectCompetitor}
            demoMode={demoMode}
            competitorImages={competitorImages}
          />
        ))}

        {canScrollDown && (
          <Flex justify="center" pt={1}>
            <IconButton
              aria-label="Show lower-ranked competitors"
              icon={<Icon as={FaChevronDown} boxSize={2.5} />}
              size="xs"
              variant="outline"
              bg={navBg}
              borderColor={navBorder}
              color={youAccent}
              _hover={{ bg: navHoverBg }}
              onClick={() => setScrollOffset((prev) => prev + 1)}
            />
          </Flex>
        )}
      </Box>
    </Panel>
  );
};

const BusinessReviewOverviewSection = ({
  business,
  scoredBusinesses = [],
  currentScoredIdx = -1,
  scoreRank = null,
  demoMode = false,
  onSelectCompetitor,
  competitorImages = {},
}) => {
  const competitorsPanelRef = useRef(null);
  const [competitorsPanelHeight, setCompetitorsPanelHeight] = useState(null);
  const sectionLabelColor = useColorModeValue('gray.500', 'gray.400');
  const tagBg = useColorModeValue('gray.50', 'gray.700');
  const tagBorder = useColorModeValue('gray.200', 'gray.600');
  const redIconBg = useColorModeValue('red.50', 'red.900');
  const redIconColor = useColorModeValue('red.500', 'red.300');
  const greenIconBg = useColorModeValue('green.50', 'green.900');
  const greenIconColor = useColorModeValue('green.500', 'green.300');
  const blueIconBg = useColorModeValue('blue.50', 'blue.900');
  const blueIconColor = useColorModeValue('blue.500', 'blue.300');
  const categoryDivider = useColorModeValue('gray.100', 'gray.700');
  const detailDivider = useColorModeValue('gray.100', 'gray.700');

  const businessTypes = sortBusinessTypes(business.types, business.primary_type);
  const hasContactDetails = Boolean(business.address || business.phone || business.website_uri);

  const showCompetitors = scoredBusinesses.length > 1 && currentScoredIdx >= 0;

  useEffect(() => {
    if (!showCompetitors) {
      setCompetitorsPanelHeight(null);
      return undefined;
    }

    const node = competitorsPanelRef.current;
    if (!node) return undefined;

    const updateHeight = () => {
      setCompetitorsPanelHeight(node.offsetHeight);
    };

    updateHeight();

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(node);

    return () => resizeObserver.disconnect();
  }, [showCompetitors, business?.maps_uri, scoredBusinesses.length, currentScoredIdx]);

  return (
    <Grid
      templateColumns={
        showCompetitors
          ? { base: '1fr', lg: 'minmax(200px, 240px) 1fr' }
          : '1fr'
      }
      gap={{ base: 4, md: 5 }}
      alignItems="start"
    >
      <Panel
        maxH={competitorsPanelHeight ? `${competitorsPanelHeight}px` : undefined}
        overflow="hidden"
      >
        {hasContactDetails && (
          <Box px={4} pt={3.5} pb={businessTypes.length > 0 ? 3 : 3.5} flexShrink={0}>
            <Text
              fontSize="2xs"
              fontWeight="700"
              color={sectionLabelColor}
              textTransform="uppercase"
              letterSpacing="0.08em"
              mb={1}
            >
              Business Details
            </Text>

            <VStack align="stretch" spacing={0} divider={<Box borderBottomWidth="1px" borderColor={detailDivider} />}>
              {business.address && (
                <DetailItem
                  icon={FaMapMarkerAlt}
                  iconBg={redIconBg}
                  iconColor={redIconColor}
                  label="Address"
                  value={business.address}
                  href={business.maps_uri}
                  isExternal
                />
              )}
              {business.phone && (
                <DetailItem
                  icon={PhoneIcon}
                  iconBg={greenIconBg}
                  iconColor={greenIconColor}
                  label="Phone"
                  value={business.phone}
                  href={`tel:${business.phone}`}
                />
              )}
              {business.website_uri && (
                <DetailItem
                  icon={FaGlobe}
                  iconBg={blueIconBg}
                  iconColor={blueIconColor}
                  label="Website"
                  value={getWebsiteLabel(business.website_uri)}
                  href={business.website_uri}
                  isExternal
                />
              )}
            </VStack>
          </Box>
        )}

        {businessTypes.length > 0 && (
          <Box
            px={4}
            py={3.5}
            borderTopWidth={hasContactDetails ? '1px' : '0'}
            borderColor={categoryDivider}
            flex={1}
            display="flex"
            flexDirection="column"
            minH={0}
            overflow="auto"
          >
            <Text
              fontSize="2xs"
              fontWeight="700"
              color={sectionLabelColor}
              textTransform="uppercase"
              letterSpacing="0.08em"
              mb={2.5}
              flexShrink={0}
            >
              Categories
            </Text>
            <Wrap spacing={2} rowGap={2} align="flex-start">
              {businessTypes.map((type) => (
                <WrapItem key={type}>
                  <Tag
                    size="md"
                    borderRadius="full"
                    bg={tagBg}
                    borderWidth="1px"
                    borderColor={tagBorder}
                    px={2.5}
                    py={1}
                    maxW="100%"
                  >
                    <TagLabel fontSize="xs" fontWeight="500" whiteSpace="normal">
                      {typeEmojis[type] || '🏢'} {formatBusinessType(type)}
                    </TagLabel>
                  </Tag>
                </WrapItem>
              ))}
            </Wrap>
          </Box>
        )}

        {!hasContactDetails && businessTypes.length === 0 && <Box flex={1} />}
      </Panel>

      {showCompetitors && (
        <CompetitorsPanel
          panelRef={competitorsPanelRef}
          business={business}
          scoredBusinesses={scoredBusinesses}
          currentScoredIdx={currentScoredIdx}
          scoreRank={scoreRank}
          demoMode={demoMode}
          onSelectCompetitor={onSelectCompetitor}
          competitorImages={competitorImages}
        />
      )}
    </Grid>
  );
};

export default BusinessReviewOverviewSection;
